import { Box, Modal, Stack, styled, Typography } from '@mui/material';
import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { Stage, Layer, Image as KonvaImage } from 'react-konva';
import { heightScreen, widthScreen } from 'const/common';
import { ButtonCustom, ButtonOutLine } from './ButtonCustom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { noteImageSvgYuq } from 'utils/validate';
import Konva from 'konva';
import { InputSelectInfinite } from './InputSelectInfinite';
import { AssetIssues } from 'types/room';
import { compact, groupBy, uniq } from 'lodash';
import { CropImage } from './CropImage/ModalCropImage';
import { Image } from './Image';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import Draggable from 'react-draggable';
import { replaceAdditionalPhotoByIndex, replaceWallPhotoByIndex } from 'services/locations';
import { useFlash } from 'contexts/flash';
import { useFilteredRoomContext } from 'hooks/useFilteredRoomContext';
import { useParams } from 'react-router-dom';

export type Marker = {
  x: number;
  y: number;
  label: string;
  value: string;
  width?: number;
  height?: number;
  idRow?: number;
  id?: number;
};
export type AssetPoint = {
  xAxis: number;
  yAxis: number;
  id: number;
  width?: number;
  height?: number;
};
export type ImageProp = {
  title?: string;
  url: string;
  tag?: string;
  onClickTag?: () => void;
  options?: AssetIssues[];
  onSavePhoto: (value: { file?: File; assets: AssetPoint[] }) => void;
  fileName?: string;
  onClickPhoto?: () => void;
  onUpdatePhotoId?: (id: number) => void;
  isSelected?: boolean;
  initMarker?: {
    x: number;
    y: number;
    id: number;
    value: string;
    width?: number;
    height?: number;
  }[];
  submittedId: number;
  wallName: string;
  wallId: number;
  isAdditional?: boolean;
  /** Wall line photos: index within that wall type (must match app replace-photo-wall-by-id). */
  inspectionPhotoOrderIndex?: number;
  /** Additional structure photos: index within that structure. */
  inspectionAdditionalOrderIndex?: number;
  /** Refetch room inspection after replacing image (new ids / urls). */
  onInspectionPhotoReplaced?: () => void;
};
type FormNote = {
  idNote: string;
};

/** Upper bound for thumbnail size (same ballpark as `elements/Image.tsx`). Display size fits image aspect ratio inside this box — no fixed landscape frame/pillarboxing. */
const MAX_THUMB_WIDTH = 265;
const MAX_THUMB_HEIGHT = 185;

export const ImageCustomSvg = ({
  title,
  url,
  tag,
  onClickTag,
  options: optionProps,
  onSavePhoto,
  onClickPhoto,
  isSelected,
  initMarker,
  submittedId,
  wallName,
  isAdditional,
  onUpdatePhotoId,
  inspectionPhotoOrderIndex,
  inspectionAdditionalOrderIndex,
  onInspectionPhotoReplaced
}: ImageProp) => {
  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors }
  } = useForm<FormNote>({ resolver: yupResolver(noteImageSvgYuq) });
  const [croppedFile, setCroppedFile] = useState<File | null>(null);
  const [open, setOpen] = useState(false);
  const [markers, setMarkers] = useState<Marker[]>([]);
  const [markerActive, setMarkerActive] = useState<Marker | null>(null);
  const [markerSelect, setMarkerSelect] = useState<number>();
  const [selectedPoint, setSelectedPoint] = useState<{ x: number; y: number } | null>(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [showCropImage, setShowCropImage] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [imageActive, setImageActive] = useState<string>(url);
  const [hoveredIndex, setHoveredIndex] = useState<number>();
  const [stageSize, setStageSize] = useState({
    width: 0,
    height: 0
  });
  const [newPhoto, setNewPhoto] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { setFlash } = useFlash();
  const { clientId } = useParams();
  const { systemDropdownValues: dropdownValueOptions } = useFilteredRoomContext(clientId);

  const dropdownValueOptionEntities = useMemo(() => {
    const entities = new Map();

    dropdownValueOptions?.forEach((option) => {
      entities.set(option.id.toString(), option.name);
    });

    return entities;
  }, [dropdownValueOptions]);

  useEffect(
    () => () => {
      dropdownValueOptionEntities.clear();
    },
    [dropdownValueOptionEntities]
  );

  useEffect(() => {
    if (!open) {
      setIsEdit(false);
    }
  }, [open, url]);

  useEffect(() => {
    if (croppedFile) {
      const objectUrl = URL.createObjectURL(croppedFile);
      setImageActive(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else {
      setImageActive(url);
    }
  }, [url, croppedFile]);

  const options = useMemo(() => {
    return (
      optionProps?.map((item: AssetIssues) => ({
        label: `${item?.name}`,
        value: item.id.toString()
      })) || []
    );
  }, [optionProps]);

  const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null);
  const stageRef = useRef<Konva.Stage | null>(null);
  const thumbStackRef = useRef<HTMLDivElement | null>(null);
  const thumbImgElRef = useRef<HTMLImageElement | null>(null);
  const [thumbGeo, setThumbGeo] = useState<{
    imgLeft: number;
    imgTop: number;
    ox: number;
    oy: number;
    dw: number;
    dh: number;
  } | null>(null);

  /** Pixel size of thumbnail `<img>` after fitting natural dimensions inside MAX_THUMB_* (no letterboxing). */
  const [thumbDisplayPx, setThumbDisplayPx] = useState<{ width: number; height: number } | null>(
    null
  );

  useLayoutEffect(() => {
    setThumbDisplayPx(null);
  }, [imageActive]);

  const recomputeThumbGeo = useCallback(() => {
    const stack = thumbStackRef.current;
    const imgEl = thumbImgElRef.current;
    if (!stack || !imgEl) return;
    const stackRect = stack.getBoundingClientRect();
    const imgRect = imgEl.getBoundingClientRect();
    const imgLeft = imgRect.left - stackRect.left + stack.scrollLeft;
    const imgTop = imgRect.top - stackRect.top + stack.scrollTop;
    const cw = imgRect.width;
    const ch = imgRect.height;
    const nw = imgEl.naturalWidth;
    const nh = imgEl.naturalHeight;
    if (!cw || !ch || !nw || !nh) return;
    const scale = Math.min(cw / nw, ch / nh);
    const dw = nw * scale;
    const dh = nh * scale;
    const ox = (cw - dw) / 2;
    const oy = (ch - dh) / 2;
    setThumbGeo({ imgLeft, imgTop, ox, oy, dw, dh });
  }, []);

  const onThumbImgLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const nw = e.currentTarget.naturalWidth;
      const nh = e.currentTarget.naturalHeight;
      if (nw > 0 && nh > 0) {
        const scale = Math.min(MAX_THUMB_WIDTH / nw, MAX_THUMB_HEIGHT / nh);
        setThumbDisplayPx({
          width: Math.round(nw * scale),
          height: Math.round(nh * scale)
        });
      }
      requestAnimationFrame(() => recomputeThumbGeo());
    },
    [recomputeThumbGeo]
  );

  useEffect(() => {
    recomputeThumbGeo();
  }, [recomputeThumbGeo, imageActive, url, thumbDisplayPx]);

  useEffect(() => {
    const el = thumbStackRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => recomputeThumbGeo());
    ro.observe(el);
    return () => ro.disconnect();
  }, [recomputeThumbGeo]);

  useEffect(() => {
    window.addEventListener('resize', recomputeThumbGeo);
    return () => window.removeEventListener('resize', recomputeThumbGeo);
  }, [recomputeThumbGeo]);

  useEffect(() => {
    if (initMarker && options.length && stageSize.width) {
      setMarkers(
        compact(
          initMarker.map((m) => {
            if (!options?.find((o) => o.value.toString() === m?.value)) return undefined;
            return {
              x: m.x * stageSize.width,
              y: m.y * stageSize.height,
              width: m.width,
              height: m.height,
              label: options.find((o) => o.value.toString() === m?.value)?.label || '',
              value: m?.value?.toString() || '',
              id: m.id
            };
          })
        )
      );
    }
  }, [initMarker, options, stageSize]);

  useEffect(() => {
    if (imageActive) {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        setImageElement(img);
      };
      img.src = imageActive;
    }
  }, [imageActive, open]);

  useEffect(() => {
    if (newPhoto) {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        setImageElement(img);
      };
      img.src = URL.createObjectURL(newPhoto);
    }
  }, [newPhoto]);

  const handleStageClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: any, marker?: Marker) => {
      if (!isEdit) return;
      setValue('idNote', '');
      if (imageElement) {
        const x = e.evt.clientX - (widthScreen - stageSize.width - 130) / 2;
        const y = e.evt.clientY - (heightScreen - stageSize.height) / 2;
        setSelectedPoint({ x, y });
        setPopupOpen(true);
      }
      if (marker) {
        setMarkerActive(marker);
      }
    },
    [imageElement, isEdit, setValue, stageSize]
  );

  useEffect(() => {
    if (markerActive?.value) {
      setValue('idNote', markerActive?.value);
    }
  }, [markerActive, setValue]);

  const handleAddText = useCallback(
    (value: FormNote) => {
      if (selectedPoint) {
        setMarkers([
          ...markers,
          {
            x: selectedPoint.x,
            y: selectedPoint.y,
            label: options.find((item) => item.value === value.idNote)?.label || '',
            value: value.idNote,
            id: Date.now()
          }
        ]);
        setSelectedPoint(null);
        setPopupOpen(false);
      }
    },
    [markers, options, selectedPoint]
  );

  const updateForm = useCallback(
    (value: FormNote) => {
      setMarkers((prevState) =>
        prevState.map((item) => {
          if (item.id === markerActive?.id) {
            return {
              ...item,
              value: value.idNote,
              label: options.find((item) => item.value === value.idNote)?.label || ''
            };
          }
          return item;
        })
      );
    },
    [markerActive?.id, options]
  );
  const handleDeleteMarker = useCallback(() => {
    setMarkers((prevState) => prevState.filter((m) => m.id !== markerActive?.id));
    setSelectedPoint(null);
    setPopupOpen(false);
  }, [markerActive?.id]);

  const handleDragEnd = (markerId: number, pos: { x: number; y: number }) => {
    setMarkers((prevMarkers) =>
      prevMarkers.map((m) => (m.id === markerId ? { ...m, x: pos.x, y: pos.y } : m))
    );
  };

  useEffect(() => {
    if (!imageElement) return;
    const computeStage = () => {
      const imgWidth = imageElement.naturalWidth || imageElement.width;
      const imgHeight = imageElement.naturalHeight || imageElement.height;
      if (!imgWidth || !imgHeight) return;
      const sidebarAndGutter = 380;
      const maxW = Math.max(260, window.innerWidth - sidebarAndGutter);
      const maxH = Math.max(220, window.innerHeight * 0.9);
      const fitScale = Math.min(maxW / imgWidth, maxH / imgHeight);
      const MAX_UPSCALE = 5;
      const scale = Math.min(fitScale, MAX_UPSCALE);
      setStageSize({
        width: Math.round(imgWidth * scale),
        height: Math.round(imgHeight * scale)
      });
    };
    computeStage();
    window.addEventListener('resize', computeStage);
    return () => window.removeEventListener('resize', computeStage);
  }, [imageElement]);
  const handleSaveImage = useCallback(async () => {
    if (stageRef.current) {
      onSavePhoto({
        assets: markers.map((item) => ({
          width: item.width,
          height: item.height,
          xAxis: item.x / stageSize.width,
          yAxis: item.y / stageSize.height,
          id: Number(item.value)
        })),
        file: croppedFile || undefined
      });
      setOpen(false);
      setMarkerSelect(undefined);
      setMarkerActive(null);
    }
  }, [croppedFile, markers, onSavePhoto, stageSize.height, stageSize.width]);

  const handleCancel = useCallback(() => {
    if (!imageActive) return;
    setIsEdit(false);
    setMarkerSelect(undefined);
    setImageActive(url);
    if (!initMarker || !initMarker.length) {
      setMarkers([]);
      return;
    }

    setMarkers(
      compact(
        initMarker.map((m) => {
          if (!options?.find((o) => o.value.toString() === m?.value)) return undefined;
          return {
            x: m.x * stageSize.width,
            y: m.y * stageSize.height,
            label: options.find((o) => o.value.toString() === m?.value)?.label || '',
            value: m?.value?.toString() || '',
            id: m.id
          };
        })
      )
    );
  }, [imageActive, url, initMarker, stageSize.width, stageSize.height, options]);

  const renderTooltipText = useCallback(
    (index: number) => {
      const optionsHover = optionProps?.find(
        (item) => item.id.toString() === markers[index || 0]?.value
      );
      if (!optionsHover) return <></>;
      const tags = compact(
        optionsHover?.tags.map((item) => {
          if (item.submittedPhotoId) {
            return item.submittedPhoto.type;
          }
          if (item.additionalStructurePhotoId) {
            return item.additionalStructurePhoto.additionalStructure.type;
          }
        })
      );

      const groupedData = groupBy(optionsHover?.submittedEntries, (item) => item.field.category);

      const lines = [
        `Asset Name: ${optionsHover?.name}`,
        `Asset Type: ${optionsHover?.type?.name}`,
        ...optionsHover.options.map(
          (op) => `${dropdownValueOptionEntities.get(op.type)}: ${op.value}`
        ),
        `Issues: ${optionsHover?.issueReports && optionsHover?.issueReports?.length > 0 ? 'Yes' : 'No'}`,
        `Model: ${optionsHover?.model?.name}`,
        `Validated: ${optionsHover?.validated ? 'Yes' : 'No'}`,
        `Tags: ${uniq(tags)?.join(', ') || ''}`
      ];
      return (
        <Stack>
          <Typography fontSize={14}>{lines.join('\n')}</Typography>
          <Stack>
            {Object.entries(groupedData).map(([title, content]) => (
              <Stack key={title}>
                <Typography fontSize={14}>{title.replaceAll('_', ' ')} :</Typography>
                {content.map((item, idx) => {
                  if (!item?.value) return <></>;
                  return (
                    <Stack key={item.id || idx} style={{ flexDirection: 'row', gap: '4px' }}>
                      <Typography fontSize={12}>{item?.field.name} : </Typography>
                      <Typography fontSize={12}>{item?.value}</Typography>
                      <Typography fontSize={12}>{item?.field?.unit}</Typography>
                    </Stack>
                  );
                })}
              </Stack>
            ))}
          </Stack>
          <Typography fontSize={14}>Photos</Typography>
          <Stack style={{ gap: 10, padding: '12px 0px' }}>
            {optionsHover?.photos?.map((item) => (
              <Image width={200} height={140} url={{ url: item.url, type: item.storage.type }} />
            ))}
          </Stack>
        </Stack>
      );
    },
    [markers, optionProps, dropdownValueOptionEntities]
  );

  const onChangePhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setNewPhoto(file);
    }
    e.target.value = '';
  };

  const onSaveNewPhoto = async () => {
    if (!newPhoto) {
      setFlash({ type: 'error', message: 'Photo not found!' });
      return;
    }
    try {
      if (isAdditional) {
        const res = await replaceAdditionalPhotoByIndex({
          submitId: submittedId,
          structureId: Number(wallName),
          orderIndex: inspectionAdditionalOrderIndex ?? 0,
          file: newPhoto
        });
        const newId = res.data?.data?.additionalStructurePhotoId;
        if (newId != null) onUpdatePhotoId?.(Number(newId));
      } else {
        const res = await replaceWallPhotoByIndex({
          submitId: submittedId,
          wall: wallName,
          orderIndex: inspectionPhotoOrderIndex ?? 0,
          file: newPhoto
        });
        const newSubmittedPhotoId = res.data?.data?.submittedPhotoId;
        if (newSubmittedPhotoId != null) onUpdatePhotoId?.(Number(newSubmittedPhotoId));
      }
      setImageActive(URL.createObjectURL(newPhoto as File));
      setNewPhoto(null);
      setFlash({ type: 'success', message: 'Update photo successful!' });
      onInspectionPhotoReplaced?.();
    } catch {
      setFlash({ type: 'error', message: 'Update photo failed!' });
    }
  };

  const onCloseModal = useCallback(() => {
    setIsEdit(false);
    setOpen(false);
    setNewPhoto(null);
    handleCancel();
  }, [handleCancel]);

  return (
    <Stack gap={1}>
      {title && (
        <Typography color={'#226AAA'} fontSize={'18px'} fontWeight={900}>
          {title}
        </Typography>
      )}
      <Stack
        ref={thumbStackRef}
        sx={{ position: 'relative', width: 'max-content', maxWidth: '100%' }}>
        <Box
          component="img"
          ref={thumbImgElRef}
          src={imageActive}
          alt={title || 'Room photo'}
          onClick={onClickPhoto}
          onLoad={onThumbImgLoad}
          sx={{
            width: thumbDisplayPx?.width ?? MAX_THUMB_WIDTH,
            height: thumbDisplayPx?.height ?? MAX_THUMB_HEIGHT,
            maxHeight: MAX_THUMB_HEIGHT,
            objectFit: 'contain',
            background: '#000',
            borderRadius: '10px',
            cursor: 'pointer',
            border: isSelected ? '5px solid #226AAA' : 'none',
            display: 'block'
          }}
        />
        {markers.map((marker, index) => {
          const canPlace =
            thumbGeo &&
            stageSize.width > 0 &&
            stageSize.height > 0 &&
            Number.isFinite(marker.x) &&
            Number.isFinite(marker.y);
          const sxThumb = canPlace
            ? {
                scaleX: thumbGeo.dw / stageSize.width,
                scaleY: thumbGeo.dh / stageSize.height
              }
            : null;
          /** One scale for marker chrome + label box: stage W/H are rounded separately so scaleX≠scaleY
           *  would distort the box and make it wider than detail → text wraps on fewer lines (e.g. GGGG vs GGG/G). */
          const thumbScale = sxThumb ? Math.min(sxThumb.scaleX, sxThumb.scaleY) : 1;
          const leftPx = canPlace
            ? thumbGeo.imgLeft + thumbGeo.ox + (marker.x / stageSize.width) * thumbGeo.dw
            : null;
          const topPx = canPlace
            ? thumbGeo.imgTop + thumbGeo.oy + (marker.y / stageSize.height) * thumbGeo.dh
            : null;
          const labelW =
            sxThumb && marker.width != null && marker.width > 0
              ? marker.width * thumbScale
              : undefined;
          const labelH =
            sxThumb && marker.height != null && marker.height > 0
              ? marker.height * thumbScale
              : undefined;
          const dotSize = sxThumb ? Math.max(4, Math.round(10 * thumbScale)) : 4;
          const gapPx = sxThumb ? Math.max(3, Math.round(5 * thumbScale)) : 5;
          const padPx = sxThumb ? Math.max(2, Math.round(4 * thumbScale)) : 4;
          let fontPx = sxThumb ? Math.max(3, Math.round(12 * thumbScale * 0.88)) : 6;
          if (sxThumb && labelH != null && labelH > 0) {
            const border = 2;
            const innerH = Math.max(0, labelH - 2 * padPx - border);
            const cap = Math.max(5, Math.floor(innerH * 0.42));
            fontPx = Math.min(fontPx, cap);
          }
          const radiusPx = sxThumb ? Math.max(3, Math.round(5 * thumbScale)) : 5;
          const minLabelW = sxThumb ? Math.max(24, Math.round(40 * thumbScale)) : 40;
          const minLabelH = sxThumb ? Math.max(16, Math.round(24 * thumbScale)) : 24;
          return (
            <Stack
              key={marker.id ?? index}
              style={{
                position: 'absolute',
                ...(leftPx != null && topPx != null
                  ? { left: leftPx, top: topPx }
                  : {
                      left: `${(marker.x / (stageSize.width || 1)) * 100}%`,
                      top: `${(marker.y / (stageSize.height || 1)) * 100}%`,
                      transform: 'translate(-50%, -50%)'
                    }),
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: `${gapPx}px`,
                width: 'max-content',
                userSelect: 'none',
                pointerEvents: 'none',
                zIndex: 1
              }}>
              <Box
                sx={{
                  width: dotSize,
                  height: dotSize,
                  backgroundColor: 'red',
                  borderRadius: '50%',
                  flexShrink: 0
                }}
              />
              <Box
                sx={{
                  position: 'relative',
                  background: 'white',
                  border: '1px solid black',
                  padding: `${padPx}px`,
                  borderRadius: `${radiusPx}px`,
                  fontSize: `${fontPx}px`,
                  lineHeight: 1.2,
                  width: labelW != null ? `${labelW}px` : 'auto',
                  height: labelH != null ? `${labelH}px` : 'auto',
                  minWidth: labelW != null ? undefined : `${minLabelW}px`,
                  minHeight: labelH != null ? undefined : `${minLabelH}px`,
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                  wordBreak: 'break-word',
                  color: '#000'
                }}>
                {marker.label}
              </Box>
            </Stack>
          );
        })}
        <img
          width={'24px'}
          height={'24px'}
          onClick={() => setOpen(true)}
          alt={'icon-room'}
          src={'/icons/icon_zoom.svg'}
          style={{ position: 'absolute', top: '10px', right: '13px', cursor: 'pointer', zIndex: 2 }}
        />
      </Stack>
      {tag && (
        <Stack
          onClick={onClickTag && onClickTag}
          style={{ cursor: 'pointer', flexDirection: 'row', gap: '7px', alignItems: 'center' }}>
          <img width={'16px'} height={'16px'} alt={'icon-tag'} src={'/icons/icon_tag.svg'} />
          <Typography color={'#000'} fontSize={'16px'} fontWeight={400}>
            {tag}
          </Typography>
        </Stack>
      )}
      <Modal open={open} onClose={onCloseModal}>
        <Stack
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-end',
            gap: '20px',
            justifyContent: 'center',
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: stageSize.width + 320,
            height: stageSize.height,
            transform: 'translate(-50%, -50%)',
            borderWidth: 0,
            outline: 'none'
          }}>
          <WrapperImage>
            {newPhoto ? (
              <BtnSave onClick={onSaveNewPhoto} />
            ) : (
              <BtnEdit
                onClick={() => {
                  inputRef.current?.setAttribute('data-type', 'outside');
                  inputRef.current?.click();
                }}
              />
            )}
            <input
              type="file"
              style={{ display: 'none' }}
              ref={inputRef}
              onChange={onChangePhoto}
            />
            <div style={{ position: 'relative' }}>
              <Stage
                ref={stageRef}
                width={stageSize.width}
                height={stageSize.height}
                style={{ zIndex: '99', position: 'relative' }}
                onContextMenu={(e) => {
                  e.evt.preventDefault();
                  if (!isEdit) return;
                  setValue('idNote', '');
                  if (imageElement) {
                    const offsetTop = imageElement.offsetTop;
                    const x = e.evt.clientX - (widthScreen - stageSize.width - 130) / 2;
                    const y = e.evt.offsetY - offsetTop;
                    setSelectedPoint({ x, y });
                    setPopupOpen(true);
                  }
                }}>
                <Layer>
                  {imageElement && (
                    <KonvaImage
                      image={imageElement}
                      width={stageSize.width}
                      height={stageSize.height}
                    />
                  )}
                </Layer>
              </Stage>
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: stageSize.width + 320,
                  height: '100%'
                }}>
                {markers.map((marker, index) => {
                  return (
                    <React.Fragment key={marker.id}>
                      <Draggable
                        defaultPosition={{ y: marker.y, x: marker.x }}
                        bounds="parent"
                        disabled={!isEdit}
                        onStart={() =>
                          setHoveredIndex(hoveredIndex !== undefined ? undefined : index)
                        }
                        onStop={(e, data) => {
                          handleDragEnd(marker.id!, {
                            x: data.x,
                            y: data.y
                          });
                        }}>
                        <div
                          onClick={() => {
                            setMarkerSelect(
                              typeof markerSelect !== 'undefined' ? undefined : index
                            );
                            setHoveredIndex(undefined);
                          }}
                          onMouseOver={() => {
                            if (markerSelect === index) return;
                            setHoveredIndex(index);
                          }}
                          onMouseOut={() => {
                            setHoveredIndex(undefined);
                          }}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            if (!isEdit) return;
                            handleStageClick({ evt: e }, { ...marker, idRow: marker.id });
                          }}
                          className="test"
                          style={{
                            userSelect: 'none',
                            width: 'max-content',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            position: 'relative',
                            zIndex: '99'
                          }}>
                          <div
                            style={{
                              background: 'red',
                              width: 10,
                              height: 10,
                              borderRadius: '50%'
                            }}
                          />
                          <div
                            style={{
                              position: 'relative',
                              background: 'white',
                              border: '1px solid black',
                              padding: '4px',
                              borderRadius: '5px',
                              fontSize: 14,
                              width: marker.width ?? 'auto',
                              height: marker.height ?? 'auto',
                              minWidth: 40,
                              minHeight: 24
                            }}>
                            {marker.label}
                            {isEdit && (
                              <div
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  const startX = e.clientX;
                                  const startY = e.clientY;
                                  const startWidth = marker.width ?? 60;
                                  const startHeight = marker.height ?? 30;
                                  const currentMarkerId = marker.id;

                                  const onMouseMove = (moveEvent: MouseEvent) => {
                                    const newWidth = Math.max(
                                      40,
                                      startWidth + (moveEvent.clientX - startX)
                                    );
                                    const newHeight = Math.max(
                                      24,
                                      startHeight + (moveEvent.clientY - startY)
                                    );

                                    setMarkers((prev) =>
                                      prev.map((m) => {
                                        if (m.id === currentMarkerId) {
                                          return {
                                            ...m,
                                            width: newWidth,
                                            height: newHeight
                                          };
                                        }
                                        return m;
                                      })
                                    );
                                  };

                                  const onMouseUp = () => {
                                    document.removeEventListener('mousemove', onMouseMove);
                                    document.removeEventListener('mouseup', onMouseUp);
                                  };

                                  document.addEventListener('mousemove', onMouseMove);
                                  document.addEventListener('mouseup', onMouseUp);
                                }}
                                style={{
                                  position: 'absolute',
                                  width: 10,
                                  height: 10,
                                  right: 0,
                                  bottom: 0,
                                  cursor: 'nwse-resize',
                                  background: '#226AAA',
                                  borderRadius: '2px'
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </Draggable>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </WrapperImage>
          {popupOpen && selectedPoint && (
            <Box
              sx={{
                position: 'absolute',
                top: selectedPoint.y,
                left: selectedPoint.x - 60,
                bgcolor: 'background.paper',
                boxShadow: 3,
                p: 2,
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                zIndex: 999
              }}>
              <Controller
                control={control}
                render={({ field: { value } }) => (
                  <InputSelectInfinite
                    onClickOption={(value) => {
                      setValue('idNote', value);
                    }}
                    handleFetchMore={() => {}}
                    hasMore={false}
                    colorTitle={'#226AAA'}
                    inputError={errors.idNote?.message as string}
                    title={'Choose Asset'}
                    value={value}
                    placeholder={'Select Asset'}
                    sx={{ padding: 0, width: 250 }}
                    options={options || []}
                  />
                )}
                name={'idNote'}
              />
              <Stack flexDirection={'row'} gap={2.5}>
                <ButtonCustom
                  style={{ height: '40px' }}
                  isSecondary
                  label={'Cancel'}
                  onClick={() => {
                    setMarkerActive(null);
                    setPopupOpen(false);
                  }}
                />
                {markerActive && (
                  <ButtonCustom
                    style={{ height: '40px' }}
                    isDelete
                    label={'Delete'}
                    onClick={() => handleDeleteMarker()}
                  />
                )}
                <ButtonCustom
                  style={{ height: '40px' }}
                  label={markerActive ? 'Update' : 'Add'}
                  onClick={handleSubmit(!markerActive ? handleAddText : updateForm)}
                />
              </Stack>
            </Box>
          )}
          <Stack gap={'20px'} height={'100%'} justifyContent={'space-between'}>
            <Stack
              sx={{
                bgcolor: '#f5f5f5',
                borderRadius: 2,
                ml: 2,
                whiteSpace: 'pre-line'
              }}>
              {(hoveredIndex !== undefined || markerSelect !== undefined) && (
                <Stack
                  sx={{
                    p: 2,
                    whiteSpace: 'pre-line',
                    position: 'absolute',
                    width: '250px',
                    bgcolor: '#fff',
                    borderRadius: '6px',
                    overflow: 'scroll',
                    height: stageSize.height - 240,
                    zIndex: 99
                  }}>
                  <Typography fontWeight={700} mb={1}>
                    Asset Info
                  </Typography>
                  {renderTooltipText(
                    hoveredIndex !== undefined ? hoveredIndex : (markerSelect ?? 0)
                  )}
                </Stack>
              )}
            </Stack>
            <Stack gap={'20px'}>
              {isEdit && (
                <ButtonCustom
                  style={{ height: '56px', width: '120px' }}
                  label={'Save'}
                  onClick={handleSaveImage}
                />
              )}
              {!isEdit && (
                <ButtonCustom
                  style={{ height: '56px', width: '120px' }}
                  isSecondary
                  label={'Edit'}
                  onClick={() => {
                    setIsEdit(true);
                  }}
                />
              )}
              {isEdit && (
                <ButtonOutLine
                  style={{ height: '56px', width: '120px' }}
                  label={'Reset'}
                  onClick={handleCancel}
                />
              )}
              {isEdit && (
                <ButtonOutLine
                  style={{ height: '56px', width: '120px' }}
                  label={'Crop Image'}
                  onClick={() => setShowCropImage(true)}
                />
              )}
            </Stack>
          </Stack>
        </Stack>
      </Modal>
      <Modal
        style={{ alignItems: 'center', justifyContent: 'center' }}
        open={showCropImage}
        onClose={() => setShowCropImage(false)}>
        <Stack
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            outline: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            width: stageSize.width + 20,
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            p: 2
          }}>
          <CropImage
            imageUrl={url}
            onCropComplete={(croppedImg) => {
              setCroppedFile(croppedImg);
              setShowCropImage(false);
            }}
            size={stageSize}
            onCancel={() => setShowCropImage(false)}
          />
        </Stack>
      </Modal>
    </Stack>
  );
};

const BtnEdit = styled(EditIcon)({
  position: 'absolute',
  top: 0,
  right: 0,
  color: 'white',
  zIndex: '9999',
  cursor: 'pointer',
  display: 'none',
  background: 'linear-gradient(rgba(50, 50, 93, 0.25), rgba(0, 0, 0, 0.3))',
  boxShadow: 'rgba(50, 50, 93, 0.25) 0px 13px 27px -5px, rgba(0, 0, 0, 0.3) 0px 8px 16px -8px'
});
const BtnSave = styled(SaveIcon)({
  position: 'absolute',
  top: 0,
  right: 0,
  color: 'white',
  zIndex: '9999',
  cursor: 'pointer',
  background: 'linear-gradient(rgba(50, 50, 93, 0.25), rgba(0, 0, 0, 0.3))',
  boxShadow: 'rgba(50, 50, 93, 0.25) 0px 13px 27px -5px, rgba(0, 0, 0, 0.3) 0px 8px 16px -8px'
});
const WrapperImage = styled(Box)({
  position: 'relative',
  width: 'fit-content',
  borderRadius: 10,
  '&:hover svg': {
    display: 'block'
  }
});
